/**
 * Checkout — Single-page checkout with mandatory Account Holder verification.
 * Enforces account holding before order completion with Supabase Auth integration.
 */

import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import {
  CheckCircle2,
  Lock,
  CreditCard,
  ArrowLeft,
  Smartphone,
  ShieldAlert,
  UserCheck,
  ArrowRight,
  MessageCircle,
} from "lucide-react";
import { useCart } from "@/features/cart/store/cart-store";
import { BreadcrumbNav } from "@/components/common/breadcrumb-nav";
import { formatPrice } from "@/components/forms/price-display";
import { siteConfig } from "@/config/site";
import { useCurrentUser } from "@/hooks/use-auth";
import { LoadingSkeleton } from "@/components/common/loading-skeleton";
import { saveUserOrder, saveAddress, type UserOrder } from "@/features/orders/store/orders-store";
import { openRazorpayMagicCheckout } from "@/lib/razorpay";
import {
  createServerRazorpayOrder,
  verifyServerPayment,
} from "@/features/checkout/services/checkout-service";
import { checkIsCustomerBlocked } from "@/features/admin/customers/store/admin-customers-store";
import { calculateOrderPricing } from "@/lib/pricing-calculator";
import { useStoreShippingSettings, useStoreTaxSettings } from "@/lib/store-settings";
import { trackMetaInitiateCheckout, trackMetaPurchase } from "@/lib/meta-pixel";

export const Route = createFileRoute("/checkout")({
  head: () => ({
    meta: [
      { title: `Checkout — ${siteConfig.name}` },
      {
        name: "description",
        content: "Complete your secure order with Aster Decorz.",
      },
      // Prerendered as static HTML (excluding it would make the Worker's SPA fallback
      // answer this URL with the homepage and a 200 — a soft 404, which is worse). But the
      // page is per-user and ~100 words, so it must not enter the index: robots.txt only
      // disallows /admin/ and /account/, and this URL is linked from the header on every
      // page, so a crawler reaches it. noindex,follow de-indexes the page while still
      // letting link equity flow through it. Deliberately noindex rather than a robots.txt
      // Disallow: a disallowed URL is never fetched, so the directive would never be read
      // and the URL could still be indexed from its internal links alone.
      { name: "robots", content: "noindex, follow" },
    ],
  }),
  component: CheckoutPage,
});

function CheckoutPage() {
  const { lines, subtotal, clear } = useCart();
  const { user, isAuthenticated, isLoading } = useCurrentUser();
  const navigate = useNavigate();

  const [completed, setCompleted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmedItems, setConfirmedItems] = useState<typeof lines>([]);
  const [confirmedTotal, setConfirmedTotal] = useState(0);
  // Set only from the server's verification response, so the reference shown on the
  // confirmation screen is exactly the one recorded in the database.
  const [confirmedOrderId, setConfirmedOrderId] = useState("");
  const [confirmedWhatsAppUrl, setConfirmedWhatsAppUrl] = useState("");
  const [blockedError, setBlockedError] = useState<string | null>(null);

  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [pincode, setPincode] = useState("");
  const [phone, setPhone] = useState("");
  const [shippingMethod, setShippingMethod] = useState<"standard" | "express">("standard");
  const [paymentMethod, setPaymentMethod] = useState<"upi" | "card" | "netbanking">("upi");

  // Pre-fill user information upon authentication & check blocked status
  useEffect(() => {
    if (user) {
      if (user.email && !email) setEmail(user.email);
      if (user.phone && !phone) setPhone(user.phone);
      if (user.name && !firstName) {
        const parts = user.name.split(" ");
        setFirstName(parts[0] || "");
        if (parts.length > 1) setLastName(parts.slice(1).join(" "));
      }
    }

    let active = true;
    const verifyBlocked = async () => {
      const activeEmail = email || user?.email;
      const activePhone = phone || user?.phone;
      const activeUserId = user?.id;

      if (!activeEmail && !activePhone && !activeUserId) {
        setBlockedError(null);
        return;
      }

      const res = await checkIsCustomerBlocked({
        email: activeEmail,
        phone: activePhone,
        userId: activeUserId,
      });

      if (active) {
        if (res.isBlocked) {
          setBlockedError(
            res.reason ||
              "Your account has been suspended by store administration. Purchases are disabled.",
          );
        } else {
          setBlockedError(null);
        }
      }
    };

    verifyBlocked();

    const handleSync = () => verifyBlocked();
    window.addEventListener("customers_updated", handleSync);
    return () => {
      active = false;
      window.removeEventListener("customers_updated", handleSync);
    };
    // firstName is only read by the one-shot prefill above (`!firstName` guard), so
    // including it merely stops the effect from depending on a stale value.
  }, [user, email, phone, firstName]);

  const shippingSettings = useStoreShippingSettings();
  const taxSettings = useStoreTaxSettings();
  const pricing = calculateOrderPricing(
    lines.map((l) => ({ price: l.product.price, qty: l.qty })),
    {
      shippingMethod,
      config: {
        standardShippingFee: shippingSettings.standardShippingFee,
        expressShippingFee: shippingSettings.expressShippingFee,
        freeShippingThreshold: shippingSettings.freeShippingThreshold,
        gstRate: taxSettings.gstRate,
        pricesIncludeGst: taxSettings.pricesIncludeGst,
      },
    },
  );
  const standardFee = pricing.isFreeShipping ? 0 : shippingSettings.standardShippingFee;
  const expressFee = shippingSettings.expressShippingFee;
  const shippingCost = pricing.shippingFee;
  const gst = pricing.gstAmount;
  const grandTotal = pricing.finalTotal;

  useEffect(() => {
    if (lines.length > 0) {
      trackMetaInitiateCheckout({
        items: lines.map((l) => ({
          id: l.product.id,
          name: l.product.name,
          price: l.product.price,
          qty: l.qty,
        })),
        total: grandTotal,
        currency: "INR",
      });
    }
  }, []);

  const handleSubmitOrder = async (e: React.FormEvent) => {
    e.preventDefault();

    // Blocked check
    const blockCheck = await checkIsCustomerBlocked({
      email: email || user?.email,
      phone: phone || user?.phone,
      userId: user?.id,
    });

    if (blockCheck.isBlocked) {
      setBlockedError(
        blockCheck.reason ||
          "Your account has been suspended by administration. Ordering is disabled.",
      );
      return;
    }

    setLoading(true);

    const itemsSummary = lines.map((l) => `${l.product.name} × ${l.qty}`).join(", ");
    const items = lines.map((l) => ({
      id: l.product.id,
      name: l.product.name,
      price: l.product.price,
      qty: l.qty,
      image: l.product.image,
    }));

    const dateStr = new Date().toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });

    const shippingAddr = {
      fullName: `${firstName} ${lastName}`.trim() || user?.name || "Customer",
      address,
      city,
      state,
      pincode,
      phone: phone || user?.phone || "",
    };

    try {
      // 1. Authoritative Server-Side Order & Price Calculation
      const serverOrder = await createServerRazorpayOrder({
        items: items.map((i) => ({ id: i.id, qty: i.qty, name: i.name, price: i.price })),
        shippingAddress: shippingAddr,
        userEmail: email || user?.email || "customer@example.com",
        userPhone: phone || user?.phone || "",
        userId: user?.id,
        shippingMethod,
      });

      // 2. Open Razorpay Checkout Popup with locked server order ID
      await openRazorpayMagicCheckout({
        key: serverOrder.keyId,
        amount: serverOrder.amount / 100,
        order_id: serverOrder.razorpayOrderId,
        items: items,
        prefill: {
          name: `${firstName} ${lastName}`.trim() || user?.name || "Customer",
          email: email || user?.email || "",
          contact: phone || user?.phone || "",
          address: address,
          city: city,
          state: state,
          pincode: pincode,
        },
        onSuccess: async (resp) => {
          // 3. Server-Side Signature Verification & Atomic Processing
          try {
            const verifyResult = await verifyServerPayment({
              razorpay_order_id: resp.razorpay_order_id || serverOrder.razorpayOrderId,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
              userId: user?.id,
              userEmail: email || user?.email || "customer@example.com",
              userPhone: phone || user?.phone || "",
              items,
              shippingAddress: shippingAddr,
              shippingMethod,
            });

            // The order reference comes back from the server. Treat a response without
            // one as a failure: there is no client-side reference to fall back on, and
            // showing a made-up one would leave the customer quoting an id support
            // cannot find.
            if (!verifyResult || !verifyResult.success || !verifyResult.orderId) {
              throw new Error("Payment verification failed on server.");
            }

            const placedOrder: UserOrder = {
              id: verifyResult.orderId,
              userId: user?.id || "",
              userEmail: email || user?.email || "customer@example.com",
              userPhone: phone || user?.phone || "",
              date: new Date().toLocaleDateString("en-IN", {
                month: "short",
                day: "numeric",
                year: "numeric",
              }),
              items: lines.map((i) => ({
                id: i.product.id,
                name: i.product.name,
                price: i.product.price,
                qty: i.qty,
                image: i.product.image,
              })),
              itemsSummary: lines.map((i) => `${i.product.name} (x${i.qty})`).join(", "),
              subtotal: serverOrder.orderSummary.subtotal,
              shippingCost: serverOrder.orderSummary.shippingFee,
              gst: serverOrder.orderSummary.gstAmount,
              total: verifyResult.total ?? serverOrder.orderSummary.total,
              status: "Processing",
              shippingAddress: shippingAddr,
              paymentMethod: "Razorpay",
            };

            await saveUserOrder(placedOrder);

            if (typeof sessionStorage !== "undefined") {
              sessionStorage.setItem("recent_order_id", verifyResult.orderId);
            }
            if (user?.id) saveAddress(user.id, shippingAddr);
            if (user?.email) saveAddress(user.email, shippingAddr);
            else if (email) saveAddress(email, shippingAddr);

            setConfirmedItems([...lines]);
            setConfirmedTotal(serverOrder.orderSummary.total);
            setConfirmedOrderId(verifyResult.orderId);
            setConfirmedWhatsAppUrl(verifyResult.whatsappOrderUrl || "");
            setLoading(false);
            setCompleted(true);

            trackMetaPurchase({
              orderId: verifyResult.orderId,
              total: verifyResult.total ?? serverOrder.orderSummary.total,
              currency: "INR",
              items: lines.map((i) => ({
                id: i.product.id,
                name: i.product.name,
                price: i.product.price,
                qty: i.qty,
              })),
            });

            clear();
          } catch (verErr: unknown) {
            console.error("Payment verification failed:", verErr);
            setLoading(false);
            const verMessage = (verErr as { message?: string }).message;
            alert(
              verMessage ||
                "Payment verification failed. If payment was deducted, please contact support with your payment ID: " +
                  resp.razorpay_payment_id,
            );
          }
        },
        onDismiss: () => {
          setLoading(false);
        },
      });
    } catch (err: unknown) {
      const message = (err as { message?: string }).message;
      alert(message || "Failed to initialize secure checkout. Please check item availability.");
      setLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="section-container py-36 max-w-4xl mx-auto">
        <LoadingSkeleton className="h-96 w-full" />
      </div>
    );
  }

  if (completed) {
    return (
      <div className="section-container max-w-3xl py-32 md:py-44 text-center">
        <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 dark:bg-emerald-950 dark:text-emerald-400">
          <CheckCircle2 className="h-10 w-10" />
        </div>
        <p className="eyebrow mt-8">Order Confirmed</p>
        <h1 className="mt-3 font-display text-4xl sm:text-5xl">Thank you for your order</h1>
        <p className="mt-4 text-sm text-muted-foreground">
          We've sent a receipt and order confirmation to{" "}
          <span className="font-medium text-foreground">{email || phone}</span>.
        </p>

        <div className="mt-10 border bg-card p-6 text-left text-sm">
          <div className="flex justify-between border-b pb-4">
            <div>
              <p className="text-xs text-muted-foreground">Order Reference</p>
              <p className="font-display text-lg">{confirmedOrderId}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Estimated Delivery</p>
              <p className="font-medium">
                {shippingMethod === "standard"
                  ? shippingSettings.standardDays
                  : shippingSettings.expressDays}{" "}
                Business Days
              </p>
            </div>
          </div>

          <div className="mt-6">
            <h3 className="eyebrow">Shipping Address</h3>
            <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
              {firstName} {lastName}
              <br />
              {address}
              <br />
              {city}, {state} {pincode}
            </p>
          </div>

          <div className="mt-6 border-t pt-4">
            <h3 className="eyebrow">Order Items</h3>
            <ul className="mt-3 divide-y">
              {confirmedItems.map(({ product, qty }) => (
                <li key={product.id} className="flex justify-between py-2 text-xs">
                  <span>
                    {product.name} × {qty}
                  </span>
                  <span className="tabular-nums">{formatPrice(product.price * qty)}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-6 border-t pt-4 flex justify-between font-display text-lg">
            <span>Total Paid</span>
            <span className="tabular-nums">{formatPrice(confirmedTotal)}</span>
          </div>
        </div>

        {/* WhatsApp Order Action Banner */}
        <div className="mt-8 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4 sm:p-5 text-left">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#25D366]/20 text-[#25D366]">
                <MessageCircle className="h-5 w-5" />
              </div>
              <div>
                <h4 className="font-display text-sm font-semibold text-foreground">
                  Order Updates via WhatsApp
                </h4>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Need quick updates or custom bouquet changes? Chat directly with our florist on
                  WhatsApp.
                </p>
              </div>
            </div>
            <a
              href={
                confirmedWhatsAppUrl ||
                `https://wa.me/919344580982?text=${encodeURIComponent(
                  `🌸 Aster Decorz Order #${confirmedOrderId}\nTotal: ₹${confirmedTotal}\nHi, I just placed this order on asterdecorz.com!`,
                )}`
              }
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-[#25D366] px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-white hover:bg-[#20ba5a] transition-colors shrink-0 shadow-sm"
            >
              <MessageCircle className="h-4 w-4 fill-white" />
              Chat on WhatsApp
            </a>
          </div>
        </div>

        <div className="mt-8 flex justify-center gap-4">
          <Link
            to="/shop"
            className="inline-flex min-h-12 items-center bg-primary px-8 text-xs tracking-[0.18em] uppercase text-primary-foreground hover:opacity-90"
          >
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="section-container pb-28 pt-20 md:pt-24">
        <div className="mb-8">
          <Link
            to="/cart"
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" /> Return to bag
          </Link>
        </div>

        {/* Optional Account Sign In / Guest Checkout Notice */}
        {!isAuthenticated ? (
          <div className="mb-6 border border-stone-200 bg-stone-50 p-4 rounded-xl">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <UserCheck className="h-5 w-5 shrink-0 text-emerald-800 mt-0.5" />
                <div>
                  <h3 className="font-display text-sm font-semibold text-foreground">
                    Instant Guest Checkout Enabled
                  </h3>
                  <p className="mt-0.5 text-xs text-muted-foreground leading-relaxed">
                    Have an existing account? Sign in to autofill your saved delivery details.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0 w-full sm:w-auto">
                <Link
                  to="/account/login"
                  search={{ redirect: "/checkout" }}
                  className="flex-1 sm:flex-none flex min-h-9 items-center justify-center bg-stone-900 px-4 text-xs uppercase tracking-wider text-white hover:bg-stone-800 font-medium rounded-lg"
                >
                  Sign In
                </Link>
                <Link
                  to="/account/register"
                  search={{ redirect: "/checkout" }}
                  className="flex-1 sm:flex-none flex min-h-9 items-center justify-center border border-stone-300 bg-white px-4 text-xs uppercase tracking-wider hover:bg-stone-100 font-medium rounded-lg text-stone-800"
                >
                  Register
                </Link>
              </div>
            </div>
          </div>
        ) : (
          <div className="mb-4 flex items-center justify-between border border-emerald-500/30 bg-emerald-500/5 p-3 rounded-lg">
            <div className="flex items-center gap-2 text-xs text-emerald-700 dark:text-emerald-400 font-semibold">
              <UserCheck className="h-4 w-4" />
              <span>
                Account Holder:{" "}
                <strong className="text-foreground">
                  {user?.name || user?.email || user?.phone}
                </strong>
              </span>
            </div>
            <span className="text-[10px] text-emerald-800 uppercase font-mono tracking-wider font-bold">
              Active
            </span>
          </div>
        )}

        <div className="grid gap-8 lg:grid-cols-12 lg:gap-12">
          {/* Left: Form */}
          <div className="lg:col-span-7">
            <form id="checkout-form" onSubmit={handleSubmitOrder} className="space-y-6">
              {/* Contact */}
              <section>
                <h2 className="font-display text-lg font-bold border-b pb-2">
                  1. Contact Information
                </h2>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <label htmlFor="email" className="eyebrow block">
                      Email Address
                    </label>
                    <input
                      id="email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="mt-1 min-h-10 w-full border border-border bg-background px-3 text-xs sm:text-sm outline-none focus:border-primary rounded-md"
                      autoComplete="email"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="phone" className="eyebrow block">
                      Phone Number
                    </label>
                    <input
                      id="phone"
                      type="tel"
                      required
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="+91"
                      className="mt-1 min-h-10 w-full border border-border bg-background px-3 text-xs sm:text-sm outline-none focus:border-primary rounded-md"
                      autoComplete="tel"
                    />
                  </div>
                </div>
              </section>

              {/* Shipping address */}
              <section>
                <h2 className="font-display text-lg font-bold border-b pb-2">
                  2. Shipping Address
                </h2>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <div>
                    <label htmlFor="fname" className="eyebrow block">
                      First Name
                    </label>
                    <input
                      id="fname"
                      type="text"
                      required
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="mt-1 min-h-10 w-full border border-border bg-background px-3 text-xs sm:text-sm outline-none focus:border-primary rounded-md"
                      autoComplete="given-name"
                    />
                  </div>
                  <div>
                    <label htmlFor="lname" className="eyebrow block">
                      Last Name
                    </label>
                    <input
                      id="lname"
                      type="text"
                      required
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="mt-1 min-h-10 w-full border border-border bg-background px-3 text-xs sm:text-sm outline-none focus:border-primary rounded-md"
                      autoComplete="family-name"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="addr" className="eyebrow block">
                      Street Address
                    </label>
                    <input
                      id="addr"
                      type="text"
                      required
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      className="mt-1 min-h-10 w-full border border-border bg-background px-3 text-xs sm:text-sm outline-none focus:border-primary rounded-md"
                      autoComplete="street-address"
                    />
                  </div>
                  <div>
                    <label htmlFor="city" className="eyebrow block">
                      City
                    </label>
                    <input
                      id="city"
                      type="text"
                      required
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      className="mt-1 min-h-10 w-full border border-border bg-background px-3 text-xs sm:text-sm outline-none focus:border-primary rounded-md"
                      autoComplete="address-level2"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="state" className="eyebrow block">
                        State
                      </label>
                      <input
                        id="state"
                        type="text"
                        required
                        value={state}
                        onChange={(e) => setState(e.target.value)}
                        className="mt-1 min-h-10 w-full border border-border bg-background px-3 text-xs sm:text-sm outline-none focus:border-primary rounded-md"
                        autoComplete="address-level1"
                      />
                    </div>
                    <div>
                      <label htmlFor="pincode" className="eyebrow block">
                        Pincode
                      </label>
                      <input
                        id="pincode"
                        type="text"
                        required
                        value={pincode}
                        onChange={(e) => setPincode(e.target.value)}
                        className="mt-1 min-h-10 w-full border border-border bg-background px-3 text-xs sm:text-sm outline-none focus:border-primary rounded-md"
                        autoComplete="postal-code"
                      />
                    </div>
                  </div>
                </div>
              </section>

              {/* Shipping method */}
              <section>
                <h2 className="font-display text-lg font-bold border-b pb-2">3. Delivery Method</h2>
                <div className="mt-3 space-y-2">
                  <label
                    className={`flex items-center justify-between border p-3 rounded-md cursor-pointer transition-colors ${
                      shippingMethod === "standard"
                        ? "border-emerald-700 bg-emerald-500/5 font-semibold"
                        : "border-border"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="shipping"
                        checked={shippingMethod === "standard"}
                        onChange={() => setShippingMethod("standard")}
                      />
                      <div>
                        <p className="text-xs font-semibold">
                          Standard Delivery ({shippingSettings.standardDays} days)
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          Complimentary white-glove delivery
                        </p>
                      </div>
                    </div>
                    <span className="text-xs font-bold text-emerald-800">
                      {standardFee === 0 ? "Free" : formatPrice(standardFee)}
                    </span>
                  </label>

                  <label
                    className={`flex items-center justify-between border p-3 rounded-md cursor-pointer transition-colors ${
                      shippingMethod === "express"
                        ? "border-emerald-700 bg-emerald-500/5 font-semibold"
                        : "border-border"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="shipping"
                        checked={shippingMethod === "express"}
                        onChange={() => setShippingMethod("express")}
                      />
                      <div>
                        <p className="text-xs font-semibold">
                          Express ({shippingSettings.expressDays} days)
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          Priority climate-controlled delivery
                        </p>
                      </div>
                    </div>
                    <span className="text-xs font-bold text-emerald-800">
                      {formatPrice(expressFee)}
                    </span>
                  </label>
                </div>
              </section>

              {/* Payment */}
              <section>
                <h2 className="font-display text-lg font-bold border-b pb-2">4. Payment Method</h2>
                <div className="mt-3 space-y-2">
                  {/* UPI */}
                  <label
                    className={`flex items-center gap-3 border p-3 rounded-md cursor-pointer transition-colors ${
                      paymentMethod === "upi"
                        ? "border-emerald-700 bg-emerald-500/5 font-semibold"
                        : "border-border"
                    }`}
                  >
                    <input
                      type="radio"
                      name="payment"
                      checked={paymentMethod === "upi"}
                      onChange={() => setPaymentMethod("upi")}
                    />
                    <div className="flex items-center gap-2 text-xs font-semibold">
                      <Smartphone className="h-4 w-4 text-emerald-800" />
                      UPI (PhonePe, Google Pay, Paytm)
                    </div>
                  </label>

                  {/* Card */}
                  <div
                    className={`border p-3 rounded-md transition-colors ${
                      paymentMethod === "card"
                        ? "border-emerald-700 bg-emerald-500/5 font-semibold"
                        : "border-border"
                    }`}
                  >
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="payment"
                        checked={paymentMethod === "card"}
                        onChange={() => setPaymentMethod("card")}
                      />
                      <div className="flex items-center gap-2 text-xs font-semibold">
                        <CreditCard className="h-4 w-4 text-emerald-800" />
                        Credit / Debit Card
                      </div>
                    </label>

                    {paymentMethod === "card" && (
                      <p className="mt-3 pt-2 border-t text-[11px] leading-relaxed text-muted-foreground">
                        Card details are entered securely on Razorpay's payment screen after you
                        place your order. We never handle or store your card number.
                      </p>
                    )}
                  </div>

                  {/* Net Banking */}
                  <label
                    className={`flex items-center gap-3 border p-3 rounded-md cursor-pointer transition-colors ${
                      paymentMethod === "netbanking"
                        ? "border-emerald-700 bg-emerald-500/5 font-semibold"
                        : "border-border"
                    }`}
                  >
                    <input
                      type="radio"
                      name="payment"
                      checked={paymentMethod === "netbanking"}
                      onChange={() => setPaymentMethod("netbanking")}
                    />
                    <span className="text-xs font-semibold">Net Banking</span>
                  </label>
                </div>
              </section>
            </form>
          </div>

          {/* Right: Order summary */}
          <div className="lg:col-span-5">
            <div className="border bg-card p-5 sm:p-6 sticky top-24 rounded-2xl shadow-sm">
              <h2 className="font-display text-lg font-bold border-b pb-3 flex items-center justify-between">
                <span>In Your Bag</span>
                <span className="text-xs font-semibold text-emerald-800">
                  ({lines.length} items)
                </span>
              </h2>

              <ul className="mt-3 divide-y max-h-56 overflow-y-auto">
                {lines.map(({ product, qty }) => (
                  <li key={product.id} className="flex gap-3 py-3">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="h-14 w-12 object-cover bg-stone rounded-md"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold truncate">{product.name}</p>
                      <p className="text-[11px] text-muted-foreground">Qty: {qty}</p>
                    </div>
                    <span className="text-xs tabular-nums font-bold">
                      {formatPrice(product.price * qty)}
                    </span>
                  </li>
                ))}
              </ul>

              <div className="mt-4 border-t pt-3 space-y-1.5 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="tabular-nums font-semibold">{formatPrice(subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Shipping</span>
                  <span className="tabular-nums font-semibold text-emerald-800">
                    {shippingCost === 0 ? "Free" : formatPrice(shippingCost)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {pricing.pricesIncludeGst
                      ? `GST (${Math.round((taxSettings.gstRate || 0.18) * 100)}% included)`
                      : "GST (18%)"}
                  </span>
                  <span className="tabular-nums font-semibold text-muted-foreground">
                    {formatPrice(gst)}
                  </span>
                </div>
                <div className="flex justify-between border-t pt-3 font-display text-xl font-bold">
                  <span>Grand Total</span>
                  <span className="tabular-nums text-emerald-900">{formatPrice(grandTotal)}</span>
                </div>
              </div>

              {/* Blocked Account Warning Banner */}
              {blockedError && (
                <div className="mt-4 p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-950 text-xs flex items-start gap-3">
                  <ShieldAlert className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold text-rose-950">Account Suspended</p>
                    <p className="mt-0.5 text-rose-800 leading-relaxed">{blockedError}</p>
                  </div>
                </div>
              )}

              {/* Primary Pay / Checkout Button in Sticky Summary Card (Always visible above the fold!) */}
              <div className="mt-5">
                <button
                  type="button"
                  onClick={() => {
                    const form = document.getElementById("checkout-form") as HTMLFormElement;
                    if (form) form.requestSubmit();
                  }}
                  disabled={loading || lines.length === 0 || !!blockedError}
                  className={`min-h-13 w-full rounded-xl px-6 text-xs font-bold uppercase tracking-wider shadow-lg transition-[color,background-color,border-color,box-shadow,transform,opacity] flex items-center justify-center gap-2 ${
                    blockedError
                      ? "bg-rose-900/10 border border-rose-300 text-rose-800 cursor-not-allowed opacity-80"
                      : "bg-emerald-900 text-white hover:bg-emerald-800 active:scale-95 disabled:opacity-50 cursor-pointer"
                  }`}
                >
                  {blockedError ? (
                    <span>ORDERING SUSPENDED FOR THIS ACCOUNT</span>
                  ) : loading ? (
                    "Processing Payment..."
                  ) : (
                    <>
                      <span>Pay {formatPrice(grandTotal)} — Complete Order</span>
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </button>
              </div>

              <div className="mt-4 flex items-center justify-center gap-2 text-xs text-muted-foreground border-t pt-3">
                <Lock className="h-3.5 w-3.5 text-emerald-600" />
                <span>Checkout is safe & encrypted</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Bottom Checkout Bar on Mobile Screens (Always visible in first view on mobile) */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-stone-300 bg-white/95 p-3 backdrop-blur-md sm:hidden shadow-lg">
        <button
          type="button"
          onClick={() => {
            const form = document.getElementById("checkout-form") as HTMLFormElement;
            if (form) form.requestSubmit();
          }}
          disabled={loading || lines.length === 0 || !!blockedError}
          className="flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-emerald-900 px-6 text-xs font-bold uppercase tracking-wider text-white shadow-md active:scale-95 disabled:opacity-50"
        >
          {loading ? (
            "Processing..."
          ) : (
            <>
              <span>Pay {formatPrice(grandTotal)} — Complete Order</span>
              <ArrowRight className="h-4 w-4" />
            </>
          )}
        </button>
      </div>
    </>
  );
}
